
import { AfterViewInit, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, input, output } from '@angular/core';
import { acceptCompletion, autocompletion, CompletionContext } from "@codemirror/autocomplete";
import { indentWithTab } from "@codemirror/commands";
import { javascript, javascriptLanguage } from '@codemirror/lang-javascript';
import { EditorState, Extension } from '@codemirror/state';
import { oneDark } from '@codemirror/theme-one-dark';
import { keymap, placeholder } from "@codemirror/view";
import { basicSetup, EditorView } from 'codemirror';
import { TableModule } from 'primeng/table';
import { CodecompletionService } from 'src/app/shared/services/codecompletion.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';

export type smartCommentSingleType = "block" | "line";
export type smartCommentType = `${smartCommentSingleType}-${smartCommentSingleType}-${smartCommentSingleType}` | `${smartCommentSingleType}-${smartCommentSingleType}` | smartCommentSingleType;
export type LintError = { id: string, raw: string, code: string, evidence: string, line: number, character: number, scope: string, reason: string, a?: string, b?: string, c?: string, d?: string, }

@Component({
  selector: 'app-codemirror-dann',
  imports: [TableModule],
  standalone: true,
  templateUrl: './codemirror-dann.component.html',
  styleUrl: './codemirror-dann.component.scss'
})
export class CodemirrorDannComponent implements AfterViewInit, OnChanges {
  @Input({ required: true }) public code!: string;
  public readonly rows = input<number>(5);
  public readonly variables = input<string[]>([]);
  public readonly skills = input<string[]>([]);
  public readonly stages = input<string[]>([]);
  public readonly hasBeenErrorsAlreadyDetected = input<boolean>(false);
  // eslint-disable-next-line @angular-eslint/no-output-native
  public readonly finish = output<{
    code: string;
    detectedErrors: boolean;
}>();
  public readonly jshintCompile = output<boolean>();
  @ViewChild('codemirror') protected codemirror!: ElementRef<HTMLDivElement>;
  protected autocompletion = autocompletion;
  protected javascriptLanguage = javascriptLanguage;
  protected editorView: EditorView | undefined;
  protected editorState: EditorState | undefined;
  protected detectedErrors: { line: number, errors: { reason: string, code: string }[] }[] = [];
  protected codeActionPlaceholder = $localize`:@@code-action-placeholder:Gib deinen JavaScript-Code hier ein…`;
  protected codeExtension: Extension[] = [];

  constructor(
    private rss: RouteShareService,
    private codeService: CodecompletionService
  ) { }

  public ngAfterViewInit(): void {
    this.codeService.initCodeCompletion(this.skills(), this.stages(), this.variables());
    const editorElement = this.codemirror.nativeElement;
    //Erste Codezeile ist immer 30,39px hoch (Zeile -> 22,3906px + Space -> 8px)
    //Jede weitere Zeile fügt 22,3906px hinzu
    const heightToRows = 8 + this.rows() * 22.3906;
    this.codeExtension = [
      basicSetup,
      keymap.of([{ key: "Tab", run: acceptCompletion }, indentWithTab]),
      placeholder(this.codeActionPlaceholder),
      javascript(),
      EditorState.readOnly.of(!this.rss.userCanEditBotConfig()),
      javascriptLanguage.data.of({ autocomplete: this.myCompletions.bind(this) }),
      oneDark,
      EditorView.theme({
        ".cm-gutters": {
          backgroundColor: "#00A599",
          color: "#fff"
        },
        ".cm-activeLineGutter": {
          backgroundColor: "#99DBD6"
        },
        ".cm-content": {
          minHeight: heightToRows + "px"
        },
        ".cm-gutter": {
          minHeight: heightToRows + "px"
        },
        "&.cm-editor": {
          height: "100%"
        },
        ".cm-scroller": {
          overflow: "auto"
        }
      }),
      EditorView.domEventHandlers({
        focusout: () => { this.saveToolConfig(); },
        keydown: (e: KeyboardEvent) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 's') {
            e.stopPropagation();
            e.preventDefault();
            this.saveToolConfig();
          }
        }
      })
    ];

    try {
      this.editorState = EditorState.create({
        doc: this.code,
        extensions: this.codeExtension
      });
    } catch (e) {
      console.error(e);
    }
    this.editorView = new EditorView({
      state: this.editorState,
      parent: editorElement
    });
    this.checkJsCode(this.code);
    if (!this.hasBeenErrorsAlreadyDetected() && this.detectedErrors.length > 0) this.jshintCompile.emit((this.detectedErrors.length > 0 ? true : false));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.editorView && this.editorState) {
      if (changes['code']) {
        this.editorView.setState(EditorState.create({ doc: this.code, extensions: this.codeExtension }));
        this.checkJsCode(this.code);
        if (!this.hasBeenErrorsAlreadyDetected() && this.detectedErrors.length > 0) this.jshintCompile.emit((this.detectedErrors.length > 0 ? true : false));
      }
    }
  }

  public myCompletions(context: CompletionContext) {
    const word = context.matchBefore(/[\w.]*$/);
    if (word?.from == word?.to && !context.explicit)
      return null;
    return {
      from: word?.from,
      options: this.codeService.codeCompletionOptions
    };
  }

  public insertInEditor(varName: string): void {
    if (this.editorView) {
      const cursor = this.editorView.state.selection.main.head;
      const transaction = this.editorView.state.update({
        changes: {
          from: cursor,
          insert: varName
        },
        selection: {
          anchor: cursor + 1
        },
        scrollIntoView: true
      });
      this.editorView.dispatch(transaction);
    } else {
      this.code = this.code + `${varName}\n`;
    }
    this.saveToolConfig();
  }

  public saveToolConfig() {
    if (this.editorView) {
      const currentCode = this.editorView.state.doc.toString();
      this.checkJsCode(currentCode);
      if (this.code !== currentCode) this.finish.emit({ code: currentCode, detectedErrors: (this.detectedErrors.length > 0 ? true : false) });
    }
  }

  protected checkJsCode(code: string) {
    this.detectedErrors = [];
    code = "async function jsHintTest() {\n" + code + "\n}\njsHintTest();";
    //@ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    window.JSHINT(code, { undef: true, browser: true, node: true, esversion: 13, curly: true, unused: true, module: true }, { RAGAI: true, skill: true, conf: true, vars: true, UAParser: true });
    //@ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    window.JSHINT.errors.forEach((error: LintError) => {
      const errorIndex = this.detectedErrors.findIndex(e => e.line === error.line);
      if (errorIndex !== -1) {
        this.detectedErrors.at(errorIndex)?.errors.push({ reason: error.reason, code: error.code });
      } else {
        this.detectedErrors.push({ line: error.line, errors: [{ reason: error.reason, code: error.code }] });
      }
    });
    this.detectedErrors.sort((a, b) => {
      if (a.line < b.line) {
        return -1;
      }
      if (a.line > b.line) {
        return 1;
      }
      return 0;
    });
  }
}
